import React, { useState, useEffect, useMemo } from "react";
import "./AddNewTrip.css";
import "leaflet/dist/leaflet.css";
import MapView from "../../components/MapView/MapView";
import ApiManager from "../../ApiManager/ApiManager";
import ManualAddressForm from "../../components/ManualAddressForm/ManualAddressForm";
import ENDPOINTS from "../../ApiManager/Endpoints";
import { BASE_URL } from "../../ApiManager/ApiMethods";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getContrastColor } from "../../utils/GetContrastColor";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import MapInput from '../../components/MapInput';
import CustomSelect from '../../components/CustomSelect';

const AddCompletedTrip = () => {
  const navigate = useNavigate();
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading({
    fetchCompanies: false,
    fetchDrivers: false,
    fetchCars: false,
    fetchCarTypes: false,
    fetchTripTypes: false,
    fetchLabels: false,
    fetchGarages: false,
    fetchCities: false,
    getPrice: false,
    submit: false
  });

  const [passengers, setPassengers] = useState([
    { name: "", phone: "", email: "" },
  ]);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [tripTypes, setTripTypes] = useState([]);
  const [tripType, setTripType] = useState(null); // Initial state for tripType
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCarTypes, setLoadingCarTypes] = useState(false);
  const [loadingTripTypes, setLoadingTripTypes] = useState(false);
  const [loadingLables, setLoadingLabels] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]); // State for selected tags
  const [loadingPrice, setLoadingPrice] = useState(false); // State to track loading
  const [charges, setCharges] = useState([]);
  const [billingItems, setBillingItems] = useState([]);
  const [selectedBillingItems, setSelectedBillingItems] = useState([]);
  const [billingItemPayload, setBillingItemPayload] = useState([]);
  const [labels, setLabels] = useState([]); // Stores all labels
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [items, setItems] = useState([{ cost: "", id: 0 }]);
  const [cities, setCities] = useState([]);
  const [showPrices, setShowPrices] = useState(false);
  const [garageData, setGarageData] = useState([]);
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedFromCity, setSelectedFromCity] = useState(null);
  const [selectedToCity, setSelectedToCity] = useState(null);
  const [selectedCarType, setSelectedCarType] = useState(null);
  const [selectedTripType, setSelectedTripType] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);

  // City location state for autocomplete
  const [fromCityLocation, setFromCityLocation] = useState(null);
  const [toCityLocation, setToCityLocation] = useState(null);

  // Memoized options arrays
  const companyOptions = useMemo(() => companies.map(company => ({ value: company.id, label: company.name })), [companies]);
  const fromCityOptions = useMemo(() => cities.map(city => ({ value: city.name, label: city.name, location: city.location })), [cities]);
  const toCityOptions = useMemo(() => cities.map(city => ({ value: city.name, label: city.name, location: city.location })), [cities]);
  const carTypeOptions = useMemo(() => carTypes.map(carType => ({ value: carType.id, label: carType.name })), [carTypes]);
  const tripTypeOptions = useMemo(() => tripTypes.map(tripType => ({ value: tripType.id, label: tripType.name })), [tripTypes]);
  const garageOptions = useMemo(() => garageData.map(garage => ({ value: garage.id, label: garage.address })), [garageData]);
  const driverOptions = useMemo(() => drivers.map(driver => ({ value: driver.id, label: driver.firstName + " " + driver.lastName })), [drivers]);
  const carOptions = useMemo(() => cars.map(car => ({ value: car.id, label: car.vehicleNo })), [cars]);

  const fetchFile = async (imagePath) => {
    if (!imagePath) return;

    try {
      // Get a presigned URL for direct download
      const response = await ApiManager.storageGetPresignedDownloadUrl(imagePath);

      if (!response.ok) {
        throw new Error("Failed to generate presigned URL");
      }

      const result = await response.json();

      // Open the presigned URL directly in a new tab
      window.open(result.url, "_blank");
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const [mapReportingAddress, setMapReportingAddress] = useState({
    latitude: 0,
    longitude: 0,
    description: "",
  });

  const [mapDropAddress, setMapDropAddress] = useState({
    latitude: 0,
    longitude: 0,
    description: "",
  });

  const handleReportingAddressSelect = (location) => {
    setMapReportingAddress(location);
  };

  const handleDropAddressSelect = (location) => {
    setMapDropAddress(location);
  };


  const handleFileUpload = async (file, index) => {
    if (!file) return;

    try {
      // Delete existing file if there is one
      const existingFile = selectedBillingItems[index]?.imagePath;
      if (existingFile) {
        await deleteStoredFile(existingFile);
      }

      // Get a presigned URL for upload
      const presignedResponse = await ApiManager.storageGetPresignedUploadUrl({
        filename: file.name,
        directory: "images/receipts"
      });

      if (!presignedResponse.ok) {
        throw new Error("Failed to get presigned URL");
      }

      const presignedData = await presignedResponse.json();

      // Upload the file directly to the storage provider
      const uploadResponse = await fetch(presignedData.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      // Save metadata after upload using the fileName from the response
      const metadataResponse = await ApiManager.storageSaveMetadata(presignedData.fileName, presignedData.key);

      if (!metadataResponse.ok) {
        throw new Error("Failed to save metadata");
      }

      handleBillingItemChange(index, "imagePath", presignedData.file_name);
      console.log("upload result", presignedData);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const deleteStoredFile = async (fileName) => {
    try {
      const response = await ApiManager.storageDeleteFile(fileName);

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      console.log(`File ${fileName} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting file ${fileName}:`, error);
    }
  };

  const handleBillingItemChange = (index, field, value) => {
    const updatedBillingItemPayload = [...billingItemPayload];
    updatedBillingItemPayload[index][field] = value;
    setBillingItemPayload(updatedBillingItemPayload);

    const updatedBillingItems = [...selectedBillingItems];
    updatedBillingItems[index][field] = value;
    setSelectedBillingItems(updatedBillingItems);
  };

  const removeBillingItem = (index) => {
    const updatedBillingItemPayload = [...billingItemPayload];
    updatedBillingItemPayload[index]["cost"] = 0;
    setBillingItemPayload(updatedBillingItemPayload);

    const updatedBillingItems = selectedBillingItems.filter(
      (_, i) => i !== index
    );
    setSelectedBillingItems(updatedBillingItems);
  };

  const addBillingItem = () => {
    setSelectedBillingItems([
      ...selectedBillingItems,
      { billingItemId: "", cost: "", imagePath: "", notes: "" },
    ]);
    setBillingItemPayload([
      ...billingItemPayload,
      { billingItemId: "", cost: "", imagePath: "", notes: "" },
    ]);
  };

  const handleLabelSelect = (e) => {
    const selectedId = parseInt(e.target.value);
    if (!selectedLabelIds.includes(selectedId)) {
      setSelectedLabelIds([...selectedLabelIds, selectedId]);
    }
  };

  const handleRemoveLabel = (id) => {
    setSelectedLabelIds(selectedLabelIds.filter((labelId) => labelId !== id));
  };



  const handleTripTypeChange = (e) => {
    e.preventDefault();
    const selectedTripTypeId = parseInt(e.target.value); // Get the id of the selected trip type
    const selectedType = tripTypes.find(
      (type) => type.id === selectedTripTypeId
    ); // Find the tripType object
    setTripType(selectedType); // Update tripType state with the selected trip type object
  };

  const [reportingSuggestions, setReportingSuggestions] = useState([]);
  const [dropSuggestions, setDropSuggestions] = useState([]);
  const [reportingInputValue, setReportingInputValue] = useState("");
  const [dropInputValue, setDropInputValue] = useState("");
  const [shouldFetchSuggestions, setShouldFetchSuggestions] = useState(true);
  const [debouncedReportingValue, setDebouncedReportingValue] = useState("");
  const [debouncedDropValue, setDebouncedDropValue] = useState("");

  useEffect(() => {
    const fetchBillingItems = async () => {
      try {
        const params = new URLSearchParams({
          page_size: 1000,
          page: 1,
        });
        const response = await ApiManager.getAllBillingItems(params);
        if (!response.ok) throw new Error("Network response was not ok");
        const json = await response.json();
        setBillingItems(json || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setBillingItems([]);
      }
    };
    fetchBillingItems();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedReportingValue(reportingInputValue);
    }, 300);

    return () => clearTimeout(handler);
  }, [reportingInputValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDropValue(dropInputValue);
    }, 300);

    return () => clearTimeout(handler);
  }, [dropInputValue]);

  useEffect(() => {
    if (shouldFetchSuggestions && debouncedReportingValue) {
      fetchSuggestions(debouncedReportingValue, "reporting");
    }
  }, [debouncedReportingValue]);

  useEffect(() => {
    if (shouldFetchSuggestions && debouncedDropValue) {
      fetchSuggestions(debouncedDropValue, "drop");
    }
  }, [debouncedDropValue]);

  const fetchSuggestions = async (inputText, addressType) => {
    console.log("about to fecth address ....");
    try {
      const params = new URLSearchParams({
        input: inputText,
        "X-Correlation-Id": "94c11eb2-cf0a-407b-b22d-2c4cea9e4725",
        location: "28.5561,77.1", // Latitude and Longitude
        radius: 50, // Search radius in km
        strictbounds: false, // Whether to enforce strict bounds or not
      });
      const response = await ApiManager.getAddressSuggestions(params);
      const data = await response.json();
      // print("suggesstions", data)
      if (addressType === "reporting") {
        setReportingSuggestions(data.predictions || []);
      } else {
        setDropSuggestions(data.predictions || []);
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      if (addressType === "reporting") {
        setReportingSuggestions([]);
      } else {
        setDropSuggestions([]);
      }
    } finally {
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const removeItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const addNewItem = () => {
    setItems([...items, { id: 0, cost: "" }]);
  };

  const handleReportingInputChange = (e) => {
    const value = e.target.value;
    setReportingInputValue(value);
    setShouldFetchSuggestions(true); // Allow fetching new suggestions
    if (value === "") {
      setReportingSuggestions([]);
      setShouldFetchSuggestions(false);
    } else {
      setShouldFetchSuggestions(true); // Allow fetching new suggestions
    }
  };

  const handleDropInputChange = (e) => {
    const value = e.target.value;
    setDropInputValue(value);
    setShouldFetchSuggestions(true); // Allow fetching new suggestions
    if (value === "") {
      setDropSuggestions([]);
      setShouldFetchSuggestions(false);
    } else {
      setShouldFetchSuggestions(true); // Allow fetching new suggestions
    }
  };

  const handleSelectSuggestionInReportingAddress = (suggestion) => {
    setReportingSuggestions([]);
    setShouldFetchSuggestions(false);
    setReportingInputValue(suggestion.description);
    console.log(
      "new location is",
      suggestion.geometry.location.lat,
      suggestion.geometry.location.lng
    );
    setMapReportingAddress({
      latitude: suggestion.geometry.location.lat,
      longitude: suggestion.geometry.location.lng,
      description: suggestion.description,
    });
  };

  const handleSelectSuggestionInDropAddress = (suggestion) => {
    setDropSuggestions([]);
    setShouldFetchSuggestions(false);
    setDropInputValue(suggestion.description);
    console.log(
      "new location is",
      suggestion.geometry.location.lat,
      suggestion.geometry.location.lng
    );
    setMapDropAddress({
      latitude: suggestion.geometry.location.lat,
      longitude: suggestion.geometry.location.lng,
      description: suggestion.description,
    });
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        await withLoading('fetchCompanies', async (updateProgress) => {
          updateProgress(10, 'Loading companies...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getAllCompanies(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing company data...');
          setCompanies(json || []);
          updateProgress(100, 'Companies loaded');
        }, 'Loading companies...');
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanies([]);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        await withLoading('fetchDrivers', async (updateProgress) => {
          updateProgress(10, 'Loading drivers...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getAllDrivers(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing driver data...');
          setDrivers(json || []);
          updateProgress(100, 'Drivers loaded');
        }, 'Loading drivers...');
      } catch (error) {
        console.error("Error fetching drivers:", error);
        setDrivers([]);
      }
    };

    fetchDrivers();
  }, []);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        await withLoading('fetchCars', async (updateProgress) => {
          updateProgress(10, 'Loading cars...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getAllCars(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing car data...');
          setCars(json || []);
          updateProgress(100, 'Cars loaded');
        }, 'Loading cars...');
      } catch (error) {
        console.error("Error fetching cars:", error);
        setCars([]);
      }
    };

    fetchCars();
  }, []);

  useEffect(() => {
    const fetchCarTypes = async () => {
      try {
        await withLoading('fetchCarTypes', async (updateProgress) => {
          updateProgress(10, 'Loading car types...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getCarType(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing car type data...');
          setCarTypes(json || []);
          updateProgress(100, 'Car types loaded');
        }, 'Loading car types...');
      } catch (error) {
        console.error("Error fetching car types:", error);
        setCarTypes([]);
      }
    };

    fetchCarTypes();
  }, []);

  useEffect(() => {
    const fetchTripTypes = async () => {
      try {
        await withLoading('fetchTripTypes', async (updateProgress) => {
          updateProgress(10, 'Loading trip types...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getAllTripTypes(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing trip type data...');
          setTripTypes(json || []);
          updateProgress(100, 'Trip types loaded');
        }, 'Loading trip types...');
      } catch (error) {
        console.error("Error fetching trip types:", error);
        setTripTypes([]);
      }
    };

    fetchTripTypes();
  }, []);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        await withLoading('fetchLabels', async (updateProgress) => {
          updateProgress(10, 'Loading labels...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getAllLabels(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing label data...');
          setLabels(json || []);
          updateProgress(100, 'Labels loaded');
        }, 'Loading labels...');
      } catch (error) {
        console.error("Error fetching labels:", error);
        setLabels([]);
      }
    };

    fetchLabels();
  }, []);

  useEffect(() => {
    const fetchGarages = async () => {
      try {
        await withLoading('fetchGarages', async (updateProgress) => {
          updateProgress(10, 'Loading garages...');
          const params = new URLSearchParams({
            page_size: 1000,
            page: 1,
          });
          const response = await ApiManager.getAllGarages(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing garage data...');
          setGarageData(json || []);
          updateProgress(100, 'Garages loaded');
        }, 'Loading garages...');
      } catch (error) {
        console.error("Error fetching garages:", error);
        setGarageData([]);
      }
    };

    fetchGarages();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const params = new URLSearchParams({
          page_size: 1000,
          page: 1,
        });
        await withLoading('fetchCities', async (updateProgress) => {
          updateProgress(10, 'Loading cities...');
          const response = await ApiManager.getAllCities(params);
          if (!response.ok) throw new Error("Network response was not ok");
          const json = await response.json();
          updateProgress(70, 'Processing city data...');
          setCities(json || []);
          updateProgress(100, 'Cities loaded');
        }, 'Loading cities...');
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      }
    };

    fetchCities();
  }, []);

  const addPassenger = () => {
    setPassengers([...passengers, { name: "", phone: "", email: "" }]);
  };

  const addAdditionalContact = () => {
    setAdditionalContacts([
      ...additionalContacts,
      { name: "", phone: "", email: "" },
    ]);
  };

  const removeAdditionalContact = (index) => {
    const updatedContacts = additionalContacts.filter((_, i) => i !== index);
    setAdditionalContacts(updatedContacts);
  };

  const handleAdditionalContactChange = (index, field, value) => {
    const updatedContacts = [...additionalContacts];
    updatedContacts[index][field] = value;
    setAdditionalContacts(updatedContacts);
  };

  const removePassenger = (index) => {
    const updatedPassengers = passengers.filter((_, i) => i !== index);
    setPassengers(updatedPassengers);
  };

  const handlePassengerChange = (index, field, value) => {
    const updatedPassengers = [...passengers];
    updatedPassengers[index][field] = value;
    setPassengers(updatedPassengers);
  };

  const handleGetPrice = async (event) => {
    setShowPrices(true);
    setLoadingPrice(true);
    event.preventDefault();

    const form = event.target.closest("form");
    if (!form) {
      console.error("Form not found");
      setLoadingPrice(false);
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        trip_type_id: selectedTripType,
        car_type_id: selectedCarType,
        company_id: selectedCompany,
        city: selectedFromCity,
      });

      const response = await ApiManager.getCharges(queryParams);
      if (!response.ok) {
        const errorText = await response.text();
        toast.error(`Failed to fetch price: ${errorText}`);
        throw new Error("Failed to fetch price");
      }

      const data = await response.json();
      setCharges(data);

      // Update form fields with the fetched prices
      form.querySelector('[name="price"]').value = data?.rates?.base_rates || "";
      form.querySelector('[name="perExKmRate"]').value = data.rates?.extra_km_rates || "";
      form.querySelector('[name="perExTimeUnitRate"]').value =
        tripType && tripType.type === "day-km"
          ? data.rates?.extra_day_rates || ""
          : data.rates?.extra_hr_rates || "";
    } catch (err) {
      console.error("Error fetching price:", err);
      form.querySelector('[name="price"]').value = "";
      form.querySelector('[name="perExKmRate"]').value = "";
      form.querySelector('[name="perExTimeUnitRate"]').value = "";
    } finally {
      setLoadingPrice(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    await withLoading('submit', async (updateProgress) => {
      updateProgress(20, 'Preparing trip data...');
      const formData = new FormData(e.target);
      const tripData = Object.fromEntries(formData.entries());

      const reportingAddress = mapReportingAddress.description;
        const dropAddress = mapDropAddress.description;

      // Format date and time to UNIX timestamp
      const scheduledStartTime =
        new Date(tripData.scheduledStartTime).getTime() / 1000;

      const formattedTripData = {
        companyId: selectedCompany,
        fromCity: selectedFromCity,
        toCity: selectedToCity,
        carType: selectedCarType,
        tripType: selectedTripType,
        garageId: selectedGarage,
        scheduledStartTime: scheduledStartTime,
        scheduledEndTime: tripData.scheduledEndTime
          ? new Date(tripData.scheduledEndTime).getTime() / 1000
          : null,
        reportingTime: tripData.reportingTime
          ? new Date(tripData.reportingTime).getTime() / 1000
          : null,
        reportingAddress: reportingAddress,
        dropAddress: dropAddress,
        passengers: passengers.filter((p) => p.name || p.phone || p.email),
        ...((tripData.bookedByName ||
          tripData.bookedByPhone ||
          tripData.bookedByEmail) && {
          bookedBy: {
            name: tripData.bookedByName,
            phone: tripData.bookedByPhone,
            email: tripData.bookedByEmail,
          },
        }),
        driverTrip: {
          driverId: parseInt(tripData.driver),
          carId: parseInt(tripData.car),
          departTime: tripData.departTime
            ? new Date(tripData.departTime).getTime() / 1000
            : null,
          startTime: tripData.startTime
            ? new Date(tripData.startTime).getTime() / 1000
            : null,
          garageStartTime: tripData.garageStartTime
            ? new Date(tripData.garageStartTime).getTime() / 1000
            : null,
          endTime: tripData.endTime
            ? new Date(tripData.endTime).getTime() / 1000
            : null,
          garageEndTime: tripData.garageEndTime
            ? new Date(tripData.garageEndTime).getTime() / 1000
            : null,
          startKm: parseFloat(tripData.startKm),
          endKm: parseFloat(tripData.endKm),
          garageStartKm: parseFloat(tripData.garageStartKm),
          garageEndKm: parseFloat(tripData.garageEndKm),
          customerSignature: tripData.customerSignature || "",
          driverNotes: tripData.driverNotes || "",
        },
        // additionalContacts: additionalContacts.filter(c => c.name || c.phone || c.email), // Filter out empty contacts
        labels: selectedLabelIds,
        remarks: tripData.remarks || "",
        operatorNotes: tripData.operatorNotes || "",
        ...(showPrices && {charges: {
          ...charges,
          rates: {
            ...charges.rates,
            base_rates: parseFloat(tripData.price) || 0,
            extra_km_rates: parseFloat(tripData.perExKmRate) || 0,
            ...(tripType?.type === "day-km" && {
              extra_day_rates: parseFloat(tripData.perExTimeUnitRate) || 0
            }),
            ...(tripType?.type !== "day-km" && {
              extra_hr_rates: parseFloat(tripData.perExTimeUnitRate) || 0
            })
          }
        }}),
        reportingCoordinates: {
          latitude: mapReportingAddress.latitude,
          longitude: mapReportingAddress.longitude,
        },
        dropCoordinates: {
          latitude: mapDropAddress.latitude,
          longitude: mapDropAddress.longitude,
        },

        TripBillingItems: billingItemPayload
          .filter((item) => item.billingItemId) // Ensure billingItemId exists
          .map((item) => ({
            ...(item.id !== null && item.id !== undefined
              ? { id: Number(item.id) }
              : {}),
            billingItemId: Number(item.billingItemId),
            cost: parseFloat(item.cost),
            imagePath: item.imagePath || "",
            notes: item.notes || "",
          })),
      };

      console.log("Formatted Trip Data:", formattedTripData);
      updateProgress(50, 'Submitting trip data...');
      const response = await ApiManager.addCompletedTrip(formattedTripData);

      if (response.ok) {
        updateProgress(80, 'Processing response...');
        const data = await response.json();
        showToast("Trip Created successfully!");
        navigate(`/login/trips/booking-details/${data.trip.tripId}`, {
          state: {
            showToast: true,
            toastMessage: "Trip created successfully!",
          },
        });
        updateProgress(100, 'Trip created successfully');
      } else {
        const errorData = await response.json();
        console.error("Error:", errorData);
        toast.error(errorData?.error?.message || "Failed to create trip. Please try again.");
      }
    });
  };

  const showToast = (message) => {
    toast.success(message, {
      position: "top-center", //toast.POSITION.TOP_CENTER,
      autoClose: 3000, // 3 seconds
    });
  };

  if (isLoading('fetchCompanies') || isLoading('fetchDrivers') ||
      isLoading('fetchCars') || isLoading('fetchCarTypes') ||
      isLoading('fetchTripTypes') || isLoading('fetchLabels') ||
      isLoading('fetchGarages') || isLoading('fetchCities')) {
    return (
      <div className="add-completed-trip-container">
        <div className="loading-overlay">
          <div className="loading-spinner">
            <LoadingSpinner
              message={getMessage('fetchCompanies') || getMessage('fetchDrivers') || getMessage('fetchCars') ||
                      getMessage('fetchCarTypes') || getMessage('fetchTripTypes') || getMessage('fetchLabels') ||
                      getMessage('fetchGarages') || getMessage('fetchCities')}
              progress={getProgress('fetchCompanies') || getProgress('fetchDrivers') || getProgress('fetchCars') ||
                       getProgress('fetchCarTypes') || getProgress('fetchTripTypes') || getProgress('fetchLabels') ||
                       getProgress('fetchGarages') || getProgress('fetchCities')}
              showProgress={true}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="addNewTrip">
      <ToastContainer />
      <form onSubmit={handleSubmit}>
        {/* Company Section */}
        <fieldset className="fieldset">
          <legend>Company Info</legend>
          <div className="formGroup">
            <label>Company *</label>
            <CustomSelect
              name="company"
              options={companyOptions}
              value={selectedCompany}
              onChange={(selectedOption) => setSelectedCompany(selectedOption?.value || "")}
              placeholder="Select a Company"
              required
            />
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Driver and Car Info</legend>
          <div className="row">
            <div className="formGroup">
              <label>Driver *</label>
              <CustomSelect
                name="driver"
                options={driverOptions}
                value={selectedDriver}
                onChange={(selectedOption) => setSelectedDriver(selectedOption?.value || "")}
                placeholder="Select a driver"
              />
            </div>
            <div className="formGroup">
              <label>Car *</label>
              <CustomSelect
                name="car"
                options={carOptions}
                value={selectedCar}
                onChange={(selectedOption) => setSelectedCar(selectedOption?.value || "")}
                placeholder="Select a car"
              />
            </div>
          </div>
        </fieldset>

        {/* Booked By / Passenger Section */}
        <fieldset className="fieldset">
          <legend>Trip Arranger</legend>
          <div className="row">
            <div className="formGroup">
              <label>Booked By Name</label>
              <input
                type="text"
                name="bookedByName"
                placeholder="Name"
                className="input"
                required
              />
            </div>
            <div className="formGroup">
              <label>Booked By Phone Number</label>
              <input
                type="tel"
                name="bookedByPhone"
                placeholder="Phone"
                className="input"
                required
                pattern="[0-9]{10}" // For a 10-digit number
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  if (e.target.value.length > 10) {
                    e.target.value = e.target.value.slice(0, 10); // Limit to 10 digits
                  }
                }}
              />
            </div>
            <div className="formGroup">
              <label>Booked By Email</label>
              <input
                type="email"
                name="bookedByEmail"
                placeholder="Email"
                className="input"
              />
            </div>
          </div>

          {additionalContacts.map((contact, index) => (
            <div key={index} className="additionalContactsRow">
              <div className="formGroup">
                <label>Additional Contact Name</label>
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) =>
                    handleAdditionalContactChange(index, "name", e.target.value)
                  }
                  placeholder={`Contact ${index + 1} Name`}
                  className="input"
                  required
                />
              </div>
              <div className="formGroup">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) =>
                    handleAdditionalContactChange(
                      index,
                      "phone",
                      e.target.value
                    )
                  }
                  placeholder="Phone"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) =>
                    handleAdditionalContactChange(
                      index,
                      "email",
                      e.target.value
                    )
                  }
                  placeholder="Email"
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removeAdditionalContact(index)}
                className="remove-button"
              >
                x
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addAdditionalContact}
            className="custom-button"
          >
            + Add Additional Contact
          </button>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Passenger Details</legend>

          {passengers.map((passenger, index) => (
            <div key={index} className="passengerRow">
              <div className="formGroup">
                <label>Passenger Name</label>
                <input
                  type="text"
                  value={passenger.name}
                  onChange={(e) =>
                    handlePassengerChange(index, "name", e.target.value)
                  }
                  placeholder="Passenger Name"
                  className="input"
                  // required
                />
              </div>
              <div className="formGroup">
                <label>Passenger Phone Number</label>
                <input
                  type="tel"
                  value={passenger.phone}
                  onChange={(e) =>
                    handlePassengerChange(index, "phone", e.target.value)
                  }
                  placeholder="Passenger Phone"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Passenger Email</label>
                <input
                  type="email"
                  value={passenger.email}
                  onChange={(e) =>
                    handlePassengerChange(index, "email", e.target.value)
                  }
                  placeholder="Passenger Email"
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removePassenger(index)}
                className="remove-button"
              >
                X
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addPassenger}
            className="custom-button"
          >
            + Add Another Passenger
          </button>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Address Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>From City </label>
              <CustomSelect
                name="fromCity"
                options={fromCityOptions}
                value={selectedFromCity}
                onChange={(selectedOption) => {
                  setSelectedFromCity(selectedOption?.value || "");
                  // Store the city location for autocomplete
                  setFromCityLocation(selectedOption?.location || null);
                }}
                placeholder="Select a city"
              />
            </div>
            <div className="formGroup">
              <label>To City</label>
              <CustomSelect
                name="toCity"
                options={toCityOptions}
                value={selectedToCity}
                onChange={(selectedOption) => {
                  setSelectedToCity(selectedOption?.value || "");
                  // Store the city location for autocomplete
                  setToCityLocation(selectedOption?.location || null);
                }}
                placeholder="Select a city"
              />
            </div>
          </div>


          <div className="mapContainer">
              {/* Reporting Address Section */}
              <div>
                <MapInput
                  label="Reporting Address"
                  onAddressSelect={handleReportingAddressSelect}
                  initialValue={mapReportingAddress}
                  required={true}
                  name="reportingAddress"
                  cityLocation={fromCityLocation}
                />
              </div>

              {/* Drop Address Section */}
              <div>
                <MapInput
                  label="Drop Address"
                  onAddressSelect={handleDropAddressSelect}
                  initialValue={mapDropAddress}
                  name="dropAddress"
                  cityLocation={toCityLocation}
                />
              </div>
            </div>

        </fieldset>

        {/* Trip Details Section */}
        <fieldset className="fieldset">
          <legend>Trip Details</legend>
          {/* First Row */}
          <div className="row">
            <div className="formGroup">
              <label>Trip Type *</label>
              <CustomSelect
                name="tripType"
                options={tripTypeOptions}
                value={selectedTripType}
                onChange={(selectedOption) => setSelectedTripType(selectedOption?.value || "")}
                placeholder="Select a Trip Type"
                required
              />
            </div>
            <div className="formGroup">
              <label>Car Type *</label>
              <CustomSelect
                name="carType"
                options={carTypeOptions}
                value={selectedCarType}
                onChange={(selectedOption) => setSelectedCarType(selectedOption?.value || "")}
                placeholder="Select a Car Type"
                required
              />
            </div>
          </div>

          <div className="formGroup">
              <label>Garage </label>
              <CustomSelect
                name="garage"
                options={garageOptions}
                value={selectedGarage}
                onChange={(selectedOption) => setSelectedGarage(selectedOption?.value || "")}
                placeholder="Select Garage"
              />
            </div>

          {/* Second Row */}
          <div className="row">
            <div className="formGroup">
              <label>Scheduled Start Time *</label>
              <input
                type="datetime-local"
                name="scheduledStartTime"
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Scheduled End Time *</label>
              <input
                type="datetime-local"
                name="scheduledEndTime"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Reporting Time *</label>
              <input
                type="datetime-local"
                name="reportingTime"
                required
                className="input"
              />
            </div>
          </div>

          </fieldset>

          {/* forth Row */}
          <fieldset className="fieldset">
          <legend>Charges</legend>
          <div className="row">
            {showPrices && (
              <>
                <div className="formGroup">
                  <label>Price</label>
                  <input
                    type="text"
                    name="price"
                    placeholder="Enter price"
                    className="input"
                    required
                  />
                </div>
                <div className="formGroup">
                  <label>Per Extra KM rate</label>
                  <input
                    type="text"
                    name="perExKmRate"
                    placeholder="Enter extra KM rate"
                    className="input"
                    required
                  />
                </div>
                <div className="formGroup">
                  <label>
                    {tripType && tripType.type === "day-km"
                      ? "Per Extra Day rate"
                      : "Per Extra Hr rate"}
                  </label>
                  <input
                    type="text"
                    name="perExTimeUnitRate"
                    placeholder="Enter extra hour rate"
                    className="input"
                    required
                  />
                </div>
              </>
            )}
            <div className="formGroup getPriceButton">
            <button
                    type="button"
                    onClick={handleGetPrice}
                    className={`custom-button ${isLoading('getPrice') ? 'loading' : ''}`}
                    disabled={isLoading('getPrice')}
                  >
                    {isLoading('getPrice') ? (
                      <LoadingSpinner
                        size="xs"
                        inline={true}
                        message="Getting price..."
                      />
                    ) : (
                      "Get Price"
                    )}
                  </button>
            </div>
          </div>
        </fieldset>

        <fieldset className="fieldset">
            <legend>Driver Trip Details</legend>
            <div className="row">
              <div className="formGroup">
                <label>Depart Time </label>
                <input
                  type="datetime-local"
                  name="departTime"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Start Time </label>
                <input
                  type="datetime-local"
                  name="startTime"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Garage Start Time </label>
                <input
                  type="datetime-local"
                  name="garageStartTime"
                  className="input"
                />
              </div>
            </div>

            {/* Third Row */}
            <div className="row">
              <div className="formGroup">
                <label>End Time </label>
                <input
                  type="datetime-local"
                  name="endTime"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Garage End Time </label>
                <input
                  type="datetime-local"
                  name="garageEndTime"
                  className="input"
                />
              </div>
            </div>

            {/* Fourth Row */}
            <div className="row">
              <div className="formGroup">
                <label>Start KM </label>
                <input
                  type="text"
                  name="startKm"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>End KM </label>
                <input
                  type="text"
                  name="endKm"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Garage Start KM </label>
                <input
                  type="text"
                  name="garageStartKm"
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Garage End KM </label>
                <input
                  type="text"
                  name="garageEndKm"
                  className="input"
                />
              </div>
            </div>

            {/* Fifth Row */}
            <div className="row">
              <div className="formGroup">
                <label>Customer Signature</label>
                <textarea
                  name="customerSignature"
                  className="input"
                ></textarea>
              </div>
              <div className="formGroup">
                <label>Driver Notes</label>
                <textarea
                  name="driverNotes"
                  className="input"
                ></textarea>
              </div>
            </div>
          </fieldset>

        <div className="row">
          <div className="formGroup">
            <label>Remarks</label>
            <textarea
              name="remarks"
              placeholder="Enter remarks"
              className="input remarks"
              rows="3"
            />
          </div>
          <div className="formGroup">
            <label>Operator Notes</label>
            <textarea
              name="operatorNotes"
              placeholder="Enter operator notes"
              className="input operatorNotes"
              rows="3"
            />
          </div>
        </div>

        <fieldset className="fieldset">
          <legend>Billing Items</legend>
          {(selectedBillingItems || []).map((selectedBillingItem, index) => (
            <div key={index} className="passengerRow">
              <div className="formGroup">
                <label>Billing Item</label>
                <CustomSelect
                      className="input"
                      options={billingItems.map(billingItem => ({ value: billingItem.billingItemId, label: billingItem.name }))}
                      value={selectedBillingItem.billingItemId}
                      onChange={(selectedOption) => handleBillingItemChange(index, "billingItemId", selectedOption?.value || "")}
                      placeholder="Select a billing Item"
                    />
              </div>
              <div className="formGroup">
                <label>Cost</label>
                <input
                  type="number"
                  value={selectedBillingItem.cost}
                  onChange={(e) =>
                    handleBillingItemChange(index, "cost", e.target.value)
                  }
                  className="input"
                />
              </div>
              <div className="formGroup">
                <label>Receipt</label>
                {selectedBillingItem.imagePath && (
                  <button
                    type="button"
                    onClick={() => fetchFile(selectedBillingItem.imagePath)}
                    className="view-receipt-btn"
                  >
                    View Receipt
                  </button>
                )}
                <input
                  type="file"
                  className="input"
                  onChange={(e) => handleFileUpload(e.target.files[0], index)}
                />
              </div>
              <div className="formGroup">
                <label>Notes</label>
                <input
                  type="textarea"
                  value={selectedBillingItem.notes}
                  onChange={(e) =>
                    handleBillingItemChange(index, "notes", e.target.value)
                  }
                  className="input"
                />
              </div>
              <button
                type="button"
                onClick={() => removeBillingItem(index)}
                className="remove-button"
              >
                X
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addBillingItem}
            className="custom-button"
          >
            + Add Billing Item
          </button>
        </fieldset>
        <fieldset className="fieldset">
          <legend>Labels</legend>
          <div className="formGroup">
            <label>Select Labels</label>
            <CustomSelect
              options={(labels || []).map(label => ({
                value: label.id,
                label: label.name
              }))}
              value=""
              onChange={(selectedOption) => {
                if (selectedOption?.value && !selectedLabelIds.includes(selectedOption.value)) {
                  setSelectedLabelIds([...selectedLabelIds, selectedOption.value]);
                }
              }}
              placeholder="Select a Label"
            />

            {/* Display selected labels */}
            {selectedLabelIds.length > 0 && (
              <div className="selectedLabels">
                {(selectedLabelIds || []).map((id) => {
                  const label = (labels || []).find((label) => label.id === id);
                  if (!label) return null;

                  const backgroundColor = label.color;
                  const textColor = getContrastColor(backgroundColor);

                  return (
                    <div
                      key={id}
                      className="selectedLabel"
                      style={{ backgroundColor: label.color, color: textColor }}
                    >
                      {label.name}
                      <button
                        style={{ color: textColor }}
                        onClick={() => handleRemoveLabel(id)}
                      >
                        x
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </fieldset>
        {/* Submit Button */}

        <button
                    type="submit"
                    className={`submitButton ${isLoading('submit') ? 'loading' : ''}`}
                  >
                    {isLoading('submit') ? (
                      <LoadingSpinner
                        size="xs"
                        inline={true}
                        message="Creating completed Trip..."
                      />
                    ) : (
                      "Submit"
                    )}
                  </button>
      </form>
    </div>
  );
};

export default AddCompletedTrip;
